import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, EnvelopeSimple } from '@phosphor-icons/react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background-default">
      <SEO 
        title="Privacy Policy"
        description="Luster Adorn Privacy Policy - Learn how we collect, use, and protect your personal information."
        url="/privacy-policy"
      />
      
      {/* Hero */}
      <section className="bg-background-subtle py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ShieldCheck size={48} className="text-primary mx-auto mb-4" />
            <h1 className="font-display text-4xl text-secondary mb-4">Privacy Policy</h1>
            <p className="font-body text-text-secondary">
              Last updated: February 3, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="prose prose-lg max-w-none"
          >
            <p className="font-body text-text-secondary leading-relaxed mb-8">
              At Luster Adorn, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you visit lusteradorn.com, make purchases, or interact with our services.
            </p>

            <div className="space-y-10">
              {/* Information We Collect */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Information We Collect</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  We collect personal information you provide, such as your name, email address, shipping address, phone number, and payment details during account creation, orders, or customer support interactions. We also gather automatically collected data like IP address, browser type, device information, and usage patterns via cookies and analytics tools.
                </p>
              </div>

              {/* How We Use Your Information */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">How We Use Your Information</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  Your data helps us process orders, manage accounts, communicate updates, and improve our services. We use it for order fulfillment, shipping, customer service, marketing (with consent), fraud prevention, and site analytics to enhance user experience.
                </p>
              </div>

              {/* Sharing Your Information */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Sharing Your Information</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  We do not sell your personal data. We share it only with trusted third parties like payment processors, shipping providers, and service providers under strict confidentiality. We may disclose information if required by law or to protect our rights.
                </p>
              </div>

              {/* Cookies and Tracking */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Cookies and Tracking</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  Our site uses cookies for functionality, preferences, and analytics. You can manage cookie preferences through your browser settings. Third-party services like Google Analytics may track usage anonymously.
                </p>
              </div>

              {/* Data Security */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Data Security</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  We implement reasonable security measures to protect your information, including encryption for sensitive data. However, no online transmission is fully secure, so we cannot guarantee absolute security.
                </p>
              </div>

              {/* Your Rights */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Your Rights</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  You can access, update, or delete your information by contacting us. For Indian residents, we comply with the Digital Personal Data Protection Act, 2023, allowing you to withdraw consent, request correction, or erasure where applicable.
                </p>
              </div>

              {/* Changes to This Policy */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Changes to This Policy</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  We may update this policy. Changes will be posted here with the effective date. Continued use after changes constitutes acceptance.
                </p>
              </div>
            </div>

            {/* Contact */}
            <div className="mt-12 p-6 bg-background-subtle rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <EnvelopeSimple size={24} className="text-primary" />
                <h3 className="font-display text-lg text-secondary">Questions?</h3>
              </div>
              <p className="font-body text-text-secondary">
                Contact us at{' '}
                <a href="mailto:info@lusteradorn.com" className="text-primary hover:underline">
                  info@lusteradorn.com
                </a>
              </p>
            </div>

            {/* Related Links */}
            <div className="mt-8 pt-8 border-t border-border-default">
              <p className="font-body text-sm text-text-muted">
                See also:{' '}
                <Link to="/terms-of-service" className="text-primary hover:underline">
                  Terms of Service
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
